import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import { LoadingSpinner, EmptyState } from '../../components/layout/SharedComponents';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import api, { getApiErrorMessage } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { formatTimestamp as fmtTs } from '../Requisitions/constants';
import {
  ChevronLeft, Activity, MapPin, ArrowRightLeft, Package,
  Plus, Save, RotateCcw
} from 'lucide-react';
import { SortableHeader, sortData, toggleSort } from './sortUtils';
import BatchManagement from '../BatchManagement';
import StockOperationsPanel from '../StockOperationsPanel';
import TransferStockTab from './TransferStockTab';

export default function StockDetailView({ itemCode, openItem, onBack }) {
  const navigate = useNavigate();
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const fmtTz = (d) => fmtTs(d, tz);
  const fmtNum = (v) => (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  const [stockData, setStockData] = useState(null);
  const [activeTab, setActiveTab] = useState('locations');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [opLocation, setOpLocation] = useState('');
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  // Sort states
  const [locSort, setLocSort] = useState({ key: '', dir: 'asc' });
  const [mvSort, setMvSort] = useState({ key: 'timestamp', dir: 'desc' });

  // Add location dialog
  const [showAddLoc, setShowAddLoc] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [newLocCode, setNewLocCode] = useState('');
  const [newLocBin, setNewLocBin] = useState('');

  const loadStockDetail = useCallback(async (ic) => {
    try {
      const r = await api.get(`/api/inventory/stock/${ic}`);
      setStockData(r.data);
      if (r.data.locations?.length > 0 && !selectedLocation) {
        setSelectedLocation(r.data.locations[0].location_code);
        setOpLocation(r.data.locations[0].location_code);
      }
    } catch { toast.error('Failed to load stock details'); }
  }, [selectedLocation]);

  useEffect(() => { loadStockDetail(itemCode); }, [itemCode, loadStockDetail]);

  const loadMovements = useCallback(async () => {
    setMovementsLoading(true);
    try {
      const r = await api.get(`/api/inventory/movements/${itemCode}?page_size=100`);
      setMovements(r.data.data || []);
    } catch {}
    setMovementsLoading(false);
  }, [itemCode]);

  useEffect(() => { if (activeTab === 'movements') loadMovements(); }, [activeTab, loadMovements]);

  const selectLocation = (locCode) => {
    setSelectedLocation(locCode);
    setOpLocation(locCode);
  };

  const saveLocationDetails = async (loc) => {
    try {
      await api.put(`/api/inventory/stock/${itemCode}/${loc.location_code}`, {
        reorder_quantity: loc.reorder_quantity, reorder_level: loc.reorder_level,
        minimum_stock_level: loc.minimum_stock_level, maximum_usage: loc.maximum_usage,
        bin_number: loc.bin_number, unit_cost: loc.unit_cost, balance_owed: loc.balance_owed,
      });
      toast.success('Stock location details saved');
    } catch { toast.error('Save failed'); }
  };

  const addLocation = async () => {
    if (!newLocCode) { toast.error('Select a location'); return; }
    try {
      await api.post(`/api/inventory/stock/${itemCode}/add-location`, { location_code: newLocCode, bin_number: newLocBin });
      toast.success('Location added');
      setShowAddLoc(false);
      loadStockDetail(itemCode);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  if (!stockData) return <AppShell><LoadingSpinner /></AppShell>;

  const selLoc = stockData.locations?.find(l => l.location_code === selectedLocation);

  return (
    <AppShell>
      <div className="flex items-center gap-3 mb-4">
        <Button size="sm" variant="ghost" onClick={() => {
          if (openItem) {
            navigate(`/data/items?edit=${openItem}`);
          } else {
            onBack();
          }
        }}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-lg font-bold">Stock Details for Item Code: {stockData.item_code}</h1>
          <p className="text-xs text-muted-foreground">{stockData.description} | Costing: {stockData.costing_method?.toUpperCase()}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-4">
        {[
          { id: 'locations', label: 'Stock Location', icon: MapPin },
          { id: 'movements', label: 'Stock Movement', icon: Activity },
          { id: 'batches', label: 'Batches / Lots', icon: Package },
          { id: 'transfer', label: 'Transfer Stock', icon: ArrowRightLeft },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <t.icon className="h-3.5 w-3.5 inline mr-1.5" />{t.label}
          </button>
        ))}
      </div>

      {activeTab === 'locations' && (
        <div className="grid grid-cols-[1fr_300px] gap-4">
          <div>
            <div className="border rounded-xl overflow-hidden mb-4">
              <table className="w-full text-sm" data-testid="stock-locations-table">
                <thead><tr className="bg-muted/50 border-b">
                  <SortableHeader label="Location" sortKey="location_code" currentSort={locSort} onSort={toggleSort(setLocSort)} />
                  <SortableHeader label="Bin" sortKey="bin_number" currentSort={locSort} onSort={toggleSort(setLocSort)} />
                  <SortableHeader label="Description" sortKey="location_description" currentSort={locSort} onSort={toggleSort(setLocSort)} />
                  <SortableHeader label="Quantity" sortKey="current_stock" currentSort={locSort} onSort={toggleSort(setLocSort)} className="text-right" />
                  <SortableHeader label="Inventory Value" sortKey="inventory_value" currentSort={locSort} onSort={toggleSort(setLocSort)} className="text-right" />
                </tr></thead>
                <tbody>
                  {sortData((stockData.locations || []), locSort.key, locSort.dir).map(loc => (
                    <tr key={loc.location_code}
                      className={`border-b cursor-pointer transition-colors ${selectedLocation === loc.location_code ? 'bg-primary/10' : 'hover:bg-accent/20'}`}
                      onClick={() => selectLocation(loc.location_code)}>
                      <td className="px-3 py-2 font-semibold">{loc.location_code}</td>
                      <td className="px-3 py-2">{loc.bin_number || '—'}</td>
                      <td className="px-3 py-2">{loc.location_description || '—'}</td>
                      <td className="px-3 py-2 text-right">{fmtNum(loc.current_stock)}</td>
                      <td className="px-3 py-2 text-right">{fmtNum(loc.inventory_value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="border-t-2 font-semibold bg-muted/30">
                  <td colSpan={3} className="px-3 py-2 text-right">Current Stock Level</td>
                  <td className="px-3 py-2 text-right">{fmtNum(stockData.total_quantity)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(stockData.total_value)}</td>
                </tr></tfoot>
              </table>
            </div>

            <Button size="sm" variant="outline" onClick={() => {
              api.get('/api/inventory/locations').then(r => setAllLocations(r.data.data || []));
              setNewLocCode(''); setNewLocBin(''); setShowAddLoc(true);
            }}><Plus className="h-3.5 w-3.5 mr-1" /> Add Location</Button>

            {selLoc && (
              <div className="border rounded-xl p-4 mt-4" data-testid="stock-location-management">
                <h3 className="font-semibold text-sm mb-3">Stock Location Management — {selLoc.location_code} ({selLoc.location_description || '—'})</h3>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  {[
                    { key: 'reorder_quantity', label: 'Re-Order Quantity' },
                    { key: 'reorder_level', label: 'Re-Order Level' },
                    { key: 'maximum_usage', label: 'Maximum Issue' },
                    { key: 'minimum_stock_level', label: 'Minimum Stock Level' },
                    { key: 'bin_number', label: 'Bin Number', isText: true },
                    { key: 'current_stock', label: 'Current Stock Level', readOnly: true },
                  ].map(f => (
                    <div key={f.key} className="space-y-1">
                      <Label className="text-xs text-primary font-semibold">{f.label}</Label>
                      <Input
                        type={f.isText ? 'text' : 'number'}
                        step={f.isText ? undefined : '0.01'}
                        value={selLoc[f.key] ?? ''}
                        readOnly={f.readOnly}
                        onChange={e => {
                          if (f.readOnly) return;
                          const newLocs = stockData.locations.map(l =>
                            l.location_code === selLoc.location_code
                              ? { ...l, [f.key]: f.isText ? e.target.value : parseFloat(e.target.value) || 0 }
                              : l
                          );
                          setStockData(prev => ({ ...prev, locations: newLocs }));
                        }}
                        className={`h-8 text-sm ${f.readOnly ? 'bg-muted' : ''}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-primary font-semibold">Balance Owed</Label>
                    <Input type="number" step="0.01" value={selLoc.balance_owed ?? 0} readOnly className="h-8 text-sm bg-muted" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-primary font-semibold">Unit Cost</Label>
                    <Input type="number" step="0.01" value={selLoc.unit_cost ?? 0}
                      onChange={e => {
                        const newLocs = stockData.locations.map(l =>
                          l.location_code === selLoc.location_code ? { ...l, unit_cost: parseFloat(e.target.value) || 0 } : l
                        );
                        setStockData(prev => ({ ...prev, locations: newLocs }));
                      }}
                      className="h-8 text-sm" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => saveLocationDetails(selLoc)}><Save className="h-3.5 w-3.5 mr-1" /> Save Stock Location Details</Button>
                  <Button size="sm" variant="outline" onClick={() => loadStockDetail(itemCode)}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset</Button>
                </div>
              </div>
            )}
          </div>

          <StockOperationsPanel
            stockData={stockData}
            selectedItem={itemCode}
            opLocation={opLocation}
            setOpLocation={setOpLocation}
            onComplete={() => loadStockDetail(itemCode)}
          />
        </div>
      )}

      {activeTab === 'movements' && (
        <div>
          <h3 className="font-semibold text-sm mb-3">Stock Movement History</h3>
          {movementsLoading ? <LoadingSpinner /> : movements.length === 0 ? (
            <EmptyState title="No movements" description="No stock movements recorded for this item." />
          ) : (
            <div className="border rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead><tr className="bg-muted/50 border-b">
                  <SortableHeader label="Date & Time" sortKey="timestamp" currentSort={mvSort} onSort={toggleSort(setMvSort)} />
                  <SortableHeader label="Type" sortKey="type" currentSort={mvSort} onSort={toggleSort(setMvSort)} />
                  <SortableHeader label="Location" sortKey="location_code" currentSort={mvSort} onSort={toggleSort(setMvSort)} />
                  <SortableHeader label="Quantity" sortKey="quantity" currentSort={mvSort} onSort={toggleSort(setMvSort)} className="text-right" />
                  <SortableHeader label="Reference" sortKey="reference" currentSort={mvSort} onSort={toggleSort(setMvSort)} />
                  <SortableHeader label="Notes" sortKey="notes" currentSort={mvSort} onSort={toggleSort(setMvSort)} />
                  <SortableHeader label="User" sortKey="user" currentSort={mvSort} onSort={toggleSort(setMvSort)} />
                </tr></thead>
                <tbody>
                  {sortData(movements, mvSort.key, mvSort.dir).map((m, i) => {
                    const typeColors = {
                      ADJUST: 'bg-blue-100 text-blue-700', ISSUE: 'bg-red-100 text-red-700',
                      RETURN: 'bg-green-100 text-green-700', TRANSFER_IN: 'bg-purple-100 text-purple-700',
                      TRANSFER_OUT: 'bg-amber-100 text-amber-700', RECEIVE: 'bg-emerald-100 text-emerald-700',
                      RECEIVE_BATCH: 'bg-emerald-100 text-emerald-700', RECEIVE_SERIAL: 'bg-emerald-100 text-emerald-700',
                      ISSUE_SERIAL: 'bg-red-100 text-red-700', WRITE_OFF: 'bg-red-200 text-red-800',
                      RESERVE: 'bg-violet-100 text-violet-700', RELEASE: 'bg-violet-100 text-violet-700',
                      QUARANTINE: 'bg-orange-100 text-orange-700', RELEASE_HOLD: 'bg-teal-100 text-teal-700',
                      REPLENISH_ORDER: 'bg-cyan-100 text-cyan-700', SETTINGS_CHANGE: 'bg-gray-200 text-gray-700',
                    };
                    return (
                      <tr key={i} className="border-b hover:bg-accent/20">
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtTz(m.timestamp)}</td>
                        <td className="px-3 py-2"><Badge className={`text-[9px] ${typeColors[m.movement_type] || 'bg-gray-200 text-gray-700'}`}>{m.movement_type}</Badge></td>
                        <td className="px-3 py-2">{m.location_code}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${m.quantity > 0 ? 'text-green-700' : 'text-red-600'}`}>{m.quantity > 0 ? '+' : ''}{fmtNum(m.quantity)}</td>
                        <td className="px-3 py-2">{m.reference || '—'}</td>
                        <td className="px-3 py-2 max-w-[200px] truncate">{m.notes || '—'}</td>
                        <td className="px-3 py-2">{m.user}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'transfer' && (
        <TransferStockTab stockData={stockData} selectedItem={itemCode} fmtNum={fmtNum} loadStockDetail={loadStockDetail} />
      )}

      {activeTab === 'batches' && (
        <BatchManagement itemCode={itemCode} />
      )}

      {/* Add Location Dialog */}
      {showAddLoc && (
        <Dialog open onOpenChange={() => setShowAddLoc(false)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Add Stock Location</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Location</Label>
                <select value={newLocCode} onChange={e => setNewLocCode(e.target.value)}
                  className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
                  <option value="">Select location...</option>
                  {allLocations.filter(l => !stockData.locations?.some(s => s.location_code === l.location_code)).map(l => (
                    <option key={l.location_code} value={l.location_code}>{l.location_code} — {l.description}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Bin Number</Label>
                <Input value={newLocBin} onChange={e => setNewLocBin(e.target.value)} className="h-8 text-sm" />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={addLocation}>Add Location</Button>
              <Button size="sm" variant="outline" onClick={() => setShowAddLoc(false)}>Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </AppShell>
  );
}
